import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './ScoreDetails.css';
import { getTenantScoreAnalytics } from '../../../api/services/analyticsService';
import { MdArrowBack, MdChevronRight, MdLightbulbOutline } from 'react-icons/md';

const MONTH_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const PERIODS = [
  { label: '3M', monthsBack: 3 },
  { label: '6M', monthsBack: 6 },
  { label: '1Y', monthsBack: 12 },
  { label: 'All', monthsBack: 24 },
];

function displayScore(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return (Math.round(n * 10) / 10).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 1 });
}

function scoreBand(score) {
  const n = Number(score);
  if (!Number.isFinite(n)) return { label: '—', className: 'current-score-status--neutral' };
  if (n >= 80) return { label: 'Excellent', className: 'current-score-status--excellent' };
  if (n >= 65) return { label: 'Good', className: 'current-score-status--good' };
  if (n >= 50) return { label: 'Fair', className: 'current-score-status--fair' };
  if (n >= 35) return { label: 'Needs work', className: 'current-score-status--low' };
  return { label: 'Needs attention', className: 'current-score-status--risk' };
}

function formatPeriod(fromMonth, fromYear, toMonth, toYear) {
  if (!fromMonth || !fromYear || !toMonth || !toYear) return null;
  return `${MONTH_SHORT[fromMonth - 1]} ${fromYear} – ${MONTH_SHORT[toMonth - 1]} ${toYear}`;
}

function hasCountedMonths(payload) {
  const totalMonths = Number(payload?.totalMonthsCounted);
  return Number.isFinite(totalMonths) && totalMonths > 0;
}

const IMPROVEMENT_TIPS = [
  'Pay rent on or before the due date each month—on-time months improve your score the most.',
  'If you pay late, fewer late months over time will help your score recover (recent months count more).',
  'Avoid missed rent entirely; they weigh heavily and take longer to smooth out.',
  'Keep using in-app rent evidence when required so your payments are recorded consistently.',
];

const ScoreDetails = ({ onBack }) => {
  const navigate = useNavigate();
  const [selectedPeriodIndex, setSelectedPeriodIndex] = useState(2);
  const [series, setSeries] = useState(() => PERIODS.map(() => ({ data: null, failed: false })));
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const normalizeScoreData = useCallback((payload) => {
    if (!payload || typeof payload !== 'object') return null;
    const totalMonthsCounted = Number(payload.totalMonthsCounted);
    if (Number.isFinite(totalMonthsCounted) && totalMonthsCounted <= 0) {
      return { ...payload, tenantScore: 0 };
    }
    return payload;
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    const results = await Promise.all(
      PERIODS.map(async (p, i) => {
        try {
          const res = await getTenantScoreAnalytics(p.monthsBack);
          const raw = res?.data && typeof res.data === 'object' ? res.data : null;
          const data = normalizeScoreData(raw);
          return { i, data, failed: !data };
        } catch {
          return { i, data: null, failed: true };
        }
      })
    );
    const next = PERIODS.map(() => ({ data: null, failed: false }));
    results.forEach(({ i, data, failed }) => {
      next[i] = { data, failed };
    });
    setSeries(next);
    const anyOk = next.some((s) => s.data);
    if (!anyOk) {
      setLoadError('Could not load score details. Try again later.');
    } else {
      const firstOk = next.findIndex((s) => s.data);
      if (firstOk >= 0) setSelectedPeriodIndex(firstOk);
    }
    setLoading(false);
  }, [normalizeScoreData]);

  useEffect(() => {
    load();
  }, [load]);

  const handleBack = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    onBack?.();
  };

  const twelve = series[2]?.data;
  const fallbackHeader = series.find((s) => s.data)?.data;
  const headerSource = twelve ?? fallbackHeader;
  const headerScore = headerSource?.tenantScore;
  const headerBand = hasCountedMonths(headerSource)
    ? scoreBand(headerScore)
    : { label: 'No score yet', className: 'current-score-status--neutral' };
  const headerUsesTwelve = Boolean(twelve);

  const selected = series[selectedPeriodIndex];
  const selectedData = selected?.data;

  const chartPoints = useMemo(() => {
    const pts = [];
    PERIODS.forEach((p, idx) => {
      const d = series[idx]?.data;
      const y = Number(d?.tenantScore);
      if (Number.isFinite(y)) {
        pts.push({ x: pts.length, y, label: p.label, monthsBack: p.monthsBack, index: idx });
      }
    });
    return pts;
  }, [series]);

  const chartWidth = 400;
  const chartHeight = 220;
  const paddingLeft = 40;
  const paddingRight = 20;
  const paddingTop = 20;
  const paddingBottom = 28;
  const graphWidth = chartWidth - paddingLeft - paddingRight;
  const graphHeight = chartHeight - paddingTop - paddingBottom;

  const { svgPoints, smoothPath, gridLines } = useMemo(() => {
    if (chartPoints.length === 0) {
      return { svgPoints: [], smoothPath: '', gridLines: [] };
    }
    const ys = chartPoints.map((p) => p.y);
    let min = Math.max(0, Math.floor(Math.min(...ys) - 15));
    let max = Math.ceil(Math.max(...ys) + 15);
    if (max <= min) max = min + 20;

    const n = chartPoints.length;
    const svgPts = chartPoints.map((spot, i) => {
      const x = paddingLeft + (n === 1 ? graphWidth / 2 : (i / (n - 1)) * graphWidth);
      const y = paddingTop + graphHeight - ((spot.y - min) / (max - min)) * graphHeight;
      return { x, y, value: spot.y, index: spot.index };
    });

    const generateSmoothPath = (points) => {
      if (points.length < 2) return '';
      if (points.length === 2) {
        return `M ${points[0].x},${points[0].y} L ${points[1].x},${points[1].y}`;
      }
      let path = `M ${points[0].x},${points[0].y}`;
      const smoothness = 0.35;
      for (let i = 0; i < points.length - 1; i++) {
        const current = points[i];
        const nextPt = points[i + 1];
        if (i === points.length - 2) {
          path += ` L ${nextPt.x},${nextPt.y}`;
        } else {
          const afterNext = points[i + 2];
          const cp1x = current.x + (nextPt.x - current.x) * smoothness;
          const cp1y = current.y;
          const cp2x = nextPt.x - (afterNext.x - current.x) * smoothness;
          const cp2y = nextPt.y;
          path += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${nextPt.x},${nextPt.y}`;
        }
      }
      return path;
    };

    const path = generateSmoothPath(svgPts);
    const lines = [];
    const gridInterval = Math.max(10, Math.ceil((max - min) / 5 / 10) * 10);
    for (let v = min; v <= max; v += gridInterval) {
      const yPos = paddingTop + graphHeight - ((v - min) / (max - min)) * graphHeight;
      lines.push({ y: yPos, value: v });
    }

    return { svgPoints: svgPts, smoothPath: path, gridLines: lines };
  }, [chartPoints, graphHeight, graphWidth, paddingLeft, paddingTop]);

  const selectedChange =
    chartPoints.length >= 2
      ? (() => {
          const selPt = chartPoints.find((p) => p.index === selectedPeriodIndex);
          if (!selPt) return null;
          const idx = chartPoints.indexOf(selPt);
          if (idx <= 0) return null;
          const prev = chartPoints[idx - 1].y;
          const cur = selPt.y;
          const delta = Math.round((cur - prev) * 10) / 10;
          if (delta === 0) return { text: '0', up: true };
          return { text: delta > 0 ? `+${delta}` : String(delta), up: delta >= 0 };
        })()
      : null;

  return (
    <div className="score-details-page">
      <div className="score-details-header">
        <button className="back-btn" onClick={handleBack} type="button" aria-label="Back">
          <MdArrowBack />
        </button>
        <h1 className="score-details-title">Score details</h1>
      </div>

      <div className="score-details-content">
        {loading && (
          <div className="score-details-state score-details-state--loading">
            <div className="score-details-skeleton score-details-skeleton--card" />
            <div className="score-details-skeleton score-details-skeleton--card" />
          </div>
        )}

        {!loading && loadError && (
          <div className="score-details-state score-details-state--error">
            <p>{loadError}</p>
            <button type="button" className="score-details-retry" onClick={load}>
              Try again
            </button>
          </div>
        )}

        {!loading && !loadError && fallbackHeader && (
          <>
            <div className="current-score-card">
              <div className="current-score-label">
                {headerUsesTwelve ? 'Your tenant score (12-month view)' : 'Your tenant score'}
              </div>
              <div className="current-score-value">{displayScore(headerScore)}</div>
              <div className={`current-score-status ${headerBand.className}`}>Status: {headerBand.label}</div>
            </div>

            <div className="score-history-card">
              <div className="period-selector">
                {PERIODS.map((p, index) => {
                  const hasData = Boolean(series[index]?.data);
                  return (
                    <button
                      key={p.label}
                      type="button"
                      className={`period-btn ${selectedPeriodIndex === index ? 'active' : ''} ${!hasData ? 'period-btn--disabled' : ''}`}
                      disabled={!hasData}
                      onClick={() => hasData && setSelectedPeriodIndex(index)}
                    >
                      {p.label}
                    </button>
                  );
                })}
              </div>

              <div className="score-history-header">
                <div className="score-history-title-row">
                  <span className="score-history-title">Lookback comparison</span>
                  {selectedData && (
                    <>
                      <span className="score-history-current">{displayScore(selectedData.tenantScore)}</span>
                      {selectedChange != null && (
                        <span
                          className={`score-history-change ${selectedChange.up ? 'score-history-change--up' : 'score-history-change--down'}`}
                        >
                          {selectedChange.text}
                        </span>
                      )}
                    </>
                  )}
                </div>
                <div className="score-history-timeframe">
                  {selectedData
                    ? formatPeriod(selectedData.fromMonth, selectedData.fromYear, selectedData.toMonth, selectedData.toYear) ||
                      `Last ${PERIODS[selectedPeriodIndex].monthsBack} months`
                    : '—'}
                </div>
              </div>

              {chartPoints.length >= 2 && smoothPath ? (
                <div className="score-chart-container">
                  <svg width="100%" height={chartHeight} viewBox={`0 0 ${chartWidth} ${chartHeight}`}>
                    {gridLines.map((line, index) => (
                      <line
                        key={index}
                        x1={paddingLeft}
                        y1={line.y}
                        x2={chartWidth - paddingRight}
                        y2={line.y}
                        stroke="#6B7280"
                        strokeWidth="1"
                        opacity="0.15"
                      />
                    ))}
                    {gridLines.map((line, index) => (
                      <text key={index} x="10" y={line.y + 4} fill="#6B7280" fontSize="11" textAnchor="start">
                        {line.value}
                      </text>
                    ))}
                    <defs>
                      <linearGradient id="scoreDetailsGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#0474C4" stopOpacity="0.3" />
                        <stop offset="100%" stopColor="#0474C4" stopOpacity="0.04" />
                      </linearGradient>
                    </defs>
                    <path
                      d={`${smoothPath} L ${svgPoints[svgPoints.length - 1].x},${paddingTop + graphHeight} L ${svgPoints[0].x},${paddingTop + graphHeight} Z`}
                      fill="url(#scoreDetailsGradient)"
                    />
                    <path
                      d={smoothPath}
                      fill="none"
                      stroke="#0474C4"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    {svgPoints.map((point) => {
                      const isSel = point.index === selectedPeriodIndex;
                      return (
                        <circle
                          key={point.index}
                          cx={point.x}
                          cy={point.y}
                          r={isSel ? 6 : 4}
                          fill={isSel ? '#02236A' : '#0474C4'}
                          stroke="#fff"
                          strokeWidth="1.5"
                        />
                      );
                    })}
                    {chartPoints.map((pt, i) => {
                      const x =
                        paddingLeft + (chartPoints.length === 1 ? graphWidth / 2 : (i / (chartPoints.length - 1)) * graphWidth);
                      return (
                        <text key={pt.label} x={x} y={chartHeight - 8} fill="#6B7280" fontSize="11" textAnchor="middle">
                          {pt.label}
                        </text>
                      );
                    })}
                  </svg>
                </div>
              ) : chartPoints.length === 1 ? (
                <p className="score-chart-fallback">Only one lookback window returned data. Try again later or contact support if this persists.</p>
              ) : (
                <p className="score-chart-fallback">Not enough data to draw a comparison chart yet.</p>
              )}

              {selectedData && (
                <div className="score-details-breakdown">
                  <div className="score-details-breakdown-title">This period</div>
                  <ul className="score-details-breakdown-list">
                    <li>
                      <span>Months counted</span>
                      <strong>{selectedData.totalMonthsCounted ?? '—'}</strong>
                    </li>
                    <li>
                      <span>On-time</span>
                      <strong>{selectedData.onTimeCount ?? 0}</strong>
                    </li>
                    <li>
                      <span>Late</span>
                      <strong>{selectedData.lateCount ?? 0}</strong>
                    </li>
                    <li>
                      <span>Missed</span>
                      <strong>{selectedData.missedCount ?? 0}</strong>
                    </li>
                    {selectedData.halfLifeMonths != null && (
                      <li>
                        <span>Half-life (months)</span>
                        <strong>{selectedData.halfLifeMonths}</strong>
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>

            <div className="score-details-tips" id="score-improve-tips">
              <h2 className="recent-activity-title">
                <MdLightbulbOutline aria-hidden className="score-details-tips-icon" />
                How to improve
              </h2>
              <ul className="score-details-tips-list">
                {IMPROVEMENT_TIPS.map((tip) => (
                  <li key={tip}>{tip}</li>
                ))}
              </ul>
              <button
                type="button"
                className="score-details-link-btn"
                onClick={() => navigate('/tenant/payment-history')}
              >
                Open payment history
                <MdChevronRight aria-hidden />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ScoreDetails;
